import { getBootstrap } from '../offline/api';
import { getAfericaoAberta } from '../offline/api';
import { getAllEmpresas, saveEmpresas } from '../db/indexedDb';
import { getNearestCompany } from './getNearestCompany';

/**
 * Carrega empresas com bicos do servidor e salva no IndexedDB
 * @param {boolean} async - Se true, executa de forma assíncrona (não bloqueia). Se false, bloqueia até carregar.
 */
export async function loadEmpresasFromServer(async = false) {
  const logPrefix = async ? '[Bootstrap Async]' : '[Bootstrap]';
  console.log(`${logPrefix} Iniciando carregamento ${async ? 'assíncrono' : 'síncrono'} de empresas...`);
  
  const loadFunction = async () => {
    try {
      const empresas = await getBootstrap();
      console.log(`${logPrefix} Empresas carregadas do servidor:`, empresas?.length || 0);
      
      if (empresas && empresas.length > 0) {
        await saveEmpresas(empresas);
        console.log(`${logPrefix} Empresas salvas no IndexedDB`);
      }
      
      return empresas;
    } catch (error) {
      console.error(`${logPrefix} Erro ao carregar empresas:`, error);
      if (!async) {
        // Se for síncrono, lançar erro para o bootstrap tratar
        throw error;
      }
      // Se for assíncrono, apenas logar
      return null;
    }
  };

  if (async) {
    // Executar em background sem bloquear
    loadFunction().catch(err => {
      console.warn(`${logPrefix} Erro ao carregar empresas em background:`, err);
    });
    return null; // Retorna imediatamente
  } else {
    // Executar de forma síncrona (bloqueia até carregar)
    return await loadFunction();
  }
}

/**
 * Fluxo principal de bootstrap (otimizado)
 * 1. Buscar empresas do IndexedDB (rápido, não bloqueia)
 * 2. Obter geolocalização
 * 3. Calcular empresa mais próxima localmente
 * 4. Verificar aferição aberta via API
 * 5. Carregar empresas do servidor de forma assíncrona (atualizar IndexedDB)
 */
export async function bootstrapFlow() {
  console.log('[Bootstrap] Iniciando fluxo de bootstrap...');

  // 1. Buscar empresas do IndexedDB primeiro (rápido, não bloqueia)
  console.log('[Bootstrap] Buscando empresas do IndexedDB...');
  let empresas = await getAllEmpresas();
  
  // Se não tiver empresas no IndexedDB, carregar do servidor de forma síncrona (primeira vez)
  if (!empresas || empresas.length === 0) {
    console.log('[Bootstrap] Nenhuma empresa no IndexedDB, carregando do servidor (síncrono)...');
    empresas = await loadEmpresasFromServer(false); // false = síncrono, bloqueia até carregar
    
    if (!empresas || empresas.length === 0) {
      console.error('[Bootstrap] Nenhuma empresa disponível');
      return {
        empresa: null,
        afericao: null,
        foraDaArea: true
      };
    }
  } else {
    console.log('[Bootstrap] Empresas encontradas no IndexedDB:', empresas.length);
    // Carregar do servidor de forma assíncrona para atualizar (não bloqueia)
    loadEmpresasFromServer(true).catch(err => {
      console.warn('[Bootstrap] Erro ao atualizar empresas em background:', err);
    });
  }

  // 2. Obter geolocalização
  console.log('[Bootstrap] Solicitando geolocalização...');
  const position = await Promise.race([
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const error = new Error('Geolocalização não suportada');
        console.error('[Bootstrap]', error.message);
        reject(error);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          console.log('[Bootstrap] Geolocalização obtida:', {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude
          });
          resolve(pos);
        },
        (error) => {
          const errorMsg = `Erro na geolocalização: ${error.message || 'Permissão negada ou timeout'}`;
          console.error('[Bootstrap] Erro na geolocalização:', error);
          reject(new Error(errorMsg));
        },
        {
          enableHighAccuracy: false,
          timeout: 15000,
          maximumAge: 60000
        }
      );
    }),
    new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error('Timeout na geolocalização após 20 segundos'));
      }, 20000);
    })
  ]);

  const userLat = position.coords.latitude;
  const userLng = position.coords.longitude;

  // 3. Calcular empresa mais próxima localmente
  console.log('[Bootstrap] Calculando empresa mais próxima localmente...');
  const empresaProxima = getNearestCompany(empresas, userLat, userLng);

  if (!empresaProxima) {
    console.log('[Bootstrap] Nenhuma empresa encontrada no raio de 300m');
    return {
      empresa: null,
      afericao: null,
      foraDaArea: true
    };
  }

  console.log('[Bootstrap] Empresa próxima encontrada:', {
    id: empresaProxima.id || empresaProxima.Id,
    nome: empresaProxima.nome || empresaProxima.Nome
  });

  // 4. Verificar aferição aberta via API
  console.log('[Bootstrap] Verificando aferição aberta via API...');
  let afericao = null;
  const empresaId = empresaProxima.id || empresaProxima.Id;
  
  try {
    afericao = await Promise.race([
      getAfericaoAberta(empresaId),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout ao buscar aferição aberta')), 10000);
      })
    ]);
    console.log('[Bootstrap] Aferição aberta:', afericao ? 'sim' : 'não');
  } catch (error) {
    console.warn('[Bootstrap] Erro ao buscar aferição aberta:', error.message);
    // Continuar mesmo se falhar, assumindo que não há aferição aberta
    afericao = null;
  }

  console.log('[Bootstrap] Fluxo concluído com sucesso');
  return {
    empresa: empresaProxima,
    afericao,
    foraDaArea: false
  };
}
